import {
  CalendarDays,
  BriefcaseBusiness,
  ClipboardCheck,
  Inbox,
  LayoutDashboard,
  ScrollText,
  Settings2,
  ShieldCheck,
  Target,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { matchPath } from 'react-router-dom';

import type { CurrentUser, PermissionModule } from '@/api/schema';
import { can } from '@/lib/permissions';

/**
 * The navigation, and the permission each entry needs.
 *
 * One definition drives the sidebar, the mini sidebar and the breadcrumb, so a new
 * screen cannot appear in one and be missing from another.
 *
 * Note that the dashboard sits under Candidates in the interface but is gated on the
 * separate `dashboard` module: the grouping is about where people look for it, the
 * permission is about what they are allowed to see.
 */

export type NavItem = {
  label: string;
  path: string;
  module: PermissionModule;
  additionalModules?: PermissionModule[];
  icon: LucideIcon;
};

export type NavSection = {
  key: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
};

export type NavGroup = {
  label: string;
  sections: NavSection[];
};

export const NAVIGATION: NavGroup[] = [
  {
    label: 'Workspace',
    sections: [
      {
        key: 'candidates',
        label: 'Candidates',
        icon: Users,
        items: [
          {
            label: 'Dashboard',
            path: '/candidates/dashboard',
            module: 'dashboard',
            icon: LayoutDashboard,
          },
          {
            label: 'All candidates',
            path: '/candidates',
            module: 'candidates',
            icon: Users,
          },
        ],
      },
      {
        key: 'screenings',
        label: 'Screenings',
        icon: ClipboardCheck,
        items: [
          {
            label: 'Flyst screening',
            path: '/screenings/flyst',
            module: 'screenings',
            icon: ClipboardCheck,
          },
          {
            label: 'RSR screening',
            path: '/screenings/rsr',
            module: 'screenings',
            icon: ShieldCheck,
          },
        ],
      },
      {
        key: 'ats',
        label: 'ATS',
        icon: Target,
        items: [
          {
            label: 'ATS candidates',
            path: '/ats',
            module: 'ats',
            icon: Target,
          },
        ],
      },
      {
        key: 'meetings',
        label: 'Meetings',
        icon: CalendarDays,
        items: [
          {
            label: 'Calendar',
            path: '/meetings/calendar',
            module: 'meetings',
            icon: CalendarDays,
          },
          {
            label: 'Meeting log',
            path: '/meetings/log',
            module: 'meetings',
            icon: ScrollText,
          },
        ],
      },
      {
        key: 'careers',
        label: 'Careers',
        icon: BriefcaseBusiness,
        items: [
          { label: 'Vacancies', path: '/careers', module: 'careers', icon: BriefcaseBusiness },
          {
            label: 'Applications',
            path: '/careers/applications',
            module: 'careers',
            additionalModules: ['candidates'],
            icon: Inbox,
          },
        ],
      },
    ],
  },
  {
    label: 'System',
    sections: [
      {
        key: 'settings',
        label: 'Settings',
        icon: Settings2,
        items: [
          {
            label: 'User management',
            path: '/settings/users',
            module: 'settings',
            icon: Users,
          },
          {
            label: 'Roles & permissions',
            path: '/settings/roles',
            module: 'settings',
            icon: ShieldCheck,
          },
          {
            label: 'Company profile',
            path: '/settings/company',
            module: 'settings',
            icon: Settings2,
          },
          {
            label: 'Audit log',
            path: '/settings/audit',
            module: 'settings',
            icon: ScrollText,
          },
        ],
      },
    ],
  },
];

/** Every item, flattened (for breadcrumbs and for finding a landing page). */
export const ALL_ITEMS: { item: NavItem; section: NavSection; group: NavGroup }[] =
  NAVIGATION.flatMap((group) =>
    group.sections.flatMap((section) =>
      section.items.map((item) => ({ item, section, group })),
    ),
  );

/**
 * The navigation this user may actually see.
 *
 * A section with nothing visible inside it disappears entirely, rather than expanding
 * to an empty list.
 */
export function visibleNavigation(user: CurrentUser | null | undefined): NavGroup[] {
  return NAVIGATION.map((group) => ({
    ...group,
    sections: group.sections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            can(user, item.module) &&
            (item.additionalModules ?? []).every((module) => can(user, module)),
        ),
      }))
      .filter((section) => section.items.length > 0),
  })).filter((group) => group.sections.length > 0);
}

/**
 * Routes that are reachable but not listed.
 *
 * A record page has a URL and a breadcrumb but no place in the sidebar; you arrive at
 * it from a row, never from the navigation. Declaring them here rather than writing
 * them by hand in the router keeps this file the one registry of paths, and gives the
 * breadcrumb and the sidebar highlight a pattern to match a dynamic path against.
 */
export type DetailRoute = {
  /** A React Router path pattern, absolute. */
  path: string;
  module: PermissionModule;
  /** The listed screen this record belongs under. */
  owner: string;
};

export const DETAIL_ROUTES: DetailRoute[] = [
  { path: '/candidates/:id', module: 'candidates', owner: '/candidates' },
];

export function detailRouteFor(pathname: string): DetailRoute | undefined {
  return DETAIL_ROUTES.find((route) => matchPath(route.path, pathname) !== null);
}

/**
 * The breadcrumb for a path: the section it lives in, then the page itself.
 *
 * A record page borrows the trail of the list it came from. Its own name is appended
 * by the page, which is the only thing that has loaded the record.
 */
export function breadcrumbFor(pathname: string): string[] {
  const match = ALL_ITEMS.find((entry) => entry.item.path === pathname);
  if (match) return [match.section.label, match.item.label];

  const detail = detailRouteFor(pathname);
  const owner = detail && ALL_ITEMS.find((entry) => entry.item.path === detail.owner);
  return owner ? [owner.section.label, owner.item.label] : [];
}

export function navItemFor(pathname: string): NavItem | undefined {
  return ALL_ITEMS.find((entry) => entry.item.path === pathname)?.item;
}

/**
 * Where to send this user when they have not asked for anywhere in particular.
 *
 * The dashboard when they can see it, otherwise the first thing they can. A role
 * without `dashboard.view` would otherwise land on a screen it is not allowed to
 * open, which reads as the application being broken.
 */
export function landingPathFor(user: CurrentUser | null | undefined): string | null {
  const visible = visibleNavigation(user);
  const first = visible[0]?.sections[0]?.items[0];
  return first?.path ?? null;
}
